import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationsApi } from '../lib/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

// Request browser notification permission
const requestNotificationPermission = async () => {
  if (typeof Notification === 'undefined') return false;
  try {
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  } catch {
    return false;
  }
};

// Show browser push notification
const showBrowserNotification = (title, body, onClick) => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: `sg-${Date.now()}`,
      renotify: true,
      silent: false,
    });
    if (onClick) {
      notification.onclick = () => {
        window.focus();
        onClick();
        notification.close();
      };
    }
  } catch (e) {
    // Silent fail – some browsers block Notification constructor
  }
};

// Play notification sound
const playNotificationSound = () => {
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    // Create a gentle two-tone chime
    const playTone = (freq, startTime, duration) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, audioCtx.currentTime + startTime);
      gain.gain.linearRampToValueAtTime(0.15, audioCtx.currentTime + startTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + startTime + duration);
      osc.start(audioCtx.currentTime + startTime);
      osc.stop(audioCtx.currentTime + startTime + duration);
    };
    playTone(880, 0, 0.15);     // A5
    playTone(1174.66, 0.12, 0.2); // D6
  } catch (e) {
    // AudioContext not available – silent
  }
};

// Trigger vibration on mobile
const vibrateDevice = () => {
  try {
    if ('vibrate' in navigator) {
      navigator.vibrate([100, 50, 100]); // short-pause-short pattern
    }
  } catch (e) {
    // Vibrate not supported – silent
  }
};

// Combined alert: sound + vibration + browser notification
const alertNewNotification = (title, body, onClick) => {
  playNotificationSound();
  vibrateDevice();
  showBrowserNotification(title, body, onClick);
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadComments, setUnreadComments] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const prevCountsRef = useRef({ messages: 0, comments: 0 });
  const pollIntervalRef = useRef(null);
  const initialLoadDone = useRef(false);

  // Request permission on login
  useEffect(() => {
    if (isAuthenticated) {
      requestNotificationPermission().then(setNotificationsEnabled);
    }
  }, [isAuthenticated]);

  // Fetch unread counts from backend
  const fetchUnreadCounts = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadMessages(0);
      setUnreadComments(0);
      return;
    }
    try {
      const res = await notificationsApi.getUnreadCount();
      const { messages, comments } = res.data;

      // Show browser notifications for NEW items (not on initial load)
      if (initialLoadDone.current && notificationsEnabled) {
        const prevM = prevCountsRef.current.messages;
        const prevC = prevCountsRef.current.comments;
        if (messages > prevM) {
          alertNewNotification(
            'New Message',
            `You have ${messages - prevM} new message${messages - prevM > 1 ? 's' : ''}`,
            () => { window.location.href = '/messages'; }
          );
        }
        if (comments > prevC) {
          alertNewNotification(
            'New Comment',
            `You have ${comments - prevC} new comment${comments - prevC > 1 ? 's' : ''} on your gems`,
            () => { window.location.href = '/home'; }
          );
        }
      }

      prevCountsRef.current = { messages, comments };
      setUnreadMessages(messages);
      setUnreadComments(comments);
      initialLoadDone.current = true;
    } catch (err) {
      // silent – user may have just logged out
    }
  }, [isAuthenticated, notificationsEnabled]);

  // Fetch recent notifications list
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) { setNotifications([]); return; }
    try {
      const res = await notificationsApi.getNotifications(1, 30);
      setNotifications(res.data.notifications || []);
    } catch (err) {
      // silent
    }
  }, [isAuthenticated]);

  // Mark single notification read
  const markRead = useCallback(async (notificationId) => {
    try {
      await notificationsApi.markRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      fetchUnreadCounts();
    } catch (err) { /* silent */ }
  }, [fetchUnreadCounts]);

  // Mark all read (optionally by type)
  const markAllRead = useCallback(async (type = null) => {
    try {
      await notificationsApi.markAllRead(type);
      setNotifications(prev => prev.map(n =>
        (!type || n.type === type) ? { ...n, read: true } : n
      ));
      if (!type || type === 'message') setUnreadMessages(0);
      if (!type || type === 'comment') setUnreadComments(0);
      prevCountsRef.current = {
        messages: type === 'comment' ? prevCountsRef.current.messages : 0,
        comments: type === 'message' ? prevCountsRef.current.comments : 0,
      };
    } catch (err) { /* silent */ }
  }, []);

  // Polling
  useEffect(() => {
    if (isAuthenticated) {
      initialLoadDone.current = false;
      fetchUnreadCounts();
      fetchNotifications();

      pollIntervalRef.current = setInterval(() => {
        fetchUnreadCounts();
      }, 30000);

      return () => clearInterval(pollIntervalRef.current);
    } else {
      setUnreadMessages(0);
      setUnreadComments(0);
      setNotifications([]);
      initialLoadDone.current = false;
    }
  }, [isAuthenticated, fetchUnreadCounts, fetchNotifications]);

  // Refresh on window focus
  useEffect(() => {
    const handleFocus = () => {
      if (isAuthenticated) {
        fetchUnreadCounts();
        fetchNotifications();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isAuthenticated, fetchUnreadCounts, fetchNotifications]);

  const totalUnread = unreadMessages + unreadComments;

  return (
    <NotificationContext.Provider value={{
      unreadMessages,
      unreadComments,
      totalUnread,
      notifications,
      notificationsEnabled,
      fetchUnreadCounts,
      fetchNotifications,
      markRead,
      markAllRead,
      markMessagesRead: () => markAllRead('message'),
      markCommentsRead: () => markAllRead('comment'),
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
};

export default NotificationContext;
